import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TeamRankingWidget from '../../components/stats/TeamRankingWidget';
import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
} from '../../../api';

const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, { value, label });
      return;
    }

    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, { value, label });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

export default function TeamRankingBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const provider = searchParams.get('provider') || '';
  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';

  const selectedCompetitionIds = useMemo(
    () =>
      competitionIdsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    [competitionIdsParam]
  );

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );

    const widgetSeason =
    selectedSeasonsFromUrl.length > 0
      ? selectedSeasonsFromUrl.join(',')
      : null;

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');

  const [title, setTitle] = useState('Statistiques équipes');
  const [metaLoading, setMetaLoading] = useState(false);

  useEffect(() => {
    if (discipline) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('discipline', 'rugby');
    setSearchParams(nextParams, { replace: true });
  }, [discipline, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    const loadSeasons = async () => {
      if (!provider || !discipline) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
        return;
      }

      try {
        setLoadingSeasons(true);

        const { data } = await getCompetitionSeasonsForRankingBuilder({
          provider,
          discipline,
        });

        if (ignore) return;

        const rawSeasons = Array.isArray(data) ? data : [];
        const normalizedSeasons = normalizeSeasonOptions(rawSeasons);

        setSeasonOptions(normalizedSeasons);

        if (selectedSeasonsFromUrl.length) {
          setSelectedSeasons(
            normalizedSeasons.filter((option) =>
              selectedSeasonsFromUrl.includes(option.value)
            )
          );
        } else if (!seasonSinceParam && normalizedSeasons.length) {
          const latestSeason = normalizedSeasons[0];
          setSelectedSeasons([latestSeason]);

          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('season', latestSeason.value);
          setSearchParams(nextParams, { replace: true });
        }
      } catch (error) {
        if (!ignore) {
          setSeasonOptions([]);
          setSelectedSeasons([]);
        }
      } finally {
        if (!ignore) {
          setLoadingSeasons(false);
        }
      }
    };

    loadSeasons();

    return () => {
      ignore = true;
    };
  }, [provider, discipline, selectedSeasonsFromUrl, seasonSinceParam]);

  useEffect(() => {
    let ignore = false;

    const loadCompetitions = async () => {
      if (!provider || !discipline) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        return;
      }

      try {
        setLoadingCompetitions(true);
        setCompetitionsError('');

        const { data } = await getCompetitionsForRankingBuilder({
          provider,
          discipline,
          season: selectedSeasons.length
            ? selectedSeasons.map((option) => option.value).join(',')
            : undefined,
          seasonSince: seasonSinceParam || undefined,
        });

        if (ignore) return;

        const normalized = Array.isArray(data) ? data : [];
        setCompetitions(normalized);

        const preselected = normalized.filter((competition) =>
          selectedCompetitionIds.includes(String(competition._id))
        );

        setSelectedCompetitions(preselected);
      } catch (error) {
        if (!ignore) {
          setCompetitions([]);
          setSelectedCompetitions([]);
          setCompetitionsError('Impossible de charger les compétitions.');
        }
      } finally {
        if (!ignore) {
          setLoadingCompetitions(false);
        }
      }
    };

    loadCompetitions();

    return () => {
      ignore = true;
    };
  }, [provider, discipline, selectedSeasons, seasonSinceParam, selectedCompetitionIds]);

useEffect(() => {
  let ignore = false;

  const loadMeta = async () => {
    const firstCompetitionId = selectedCompetitionIds[0];

    if (!firstCompetitionId) {
      setTitle('Statistiques équipes');
      setMetaLoading(false);
      return;
    }

    try {
      setMetaLoading(true);
      const { data } = await getRankingCompetitionMeta(firstCompetitionId);

      if (!ignore) {
        setTitle(
          selectedCompetitionIds.length > 1
            ? 'Statistiques équipes'
            : data?.name || 'Statistiques équipes'
        );
      }
    } catch (error) {
      if (!ignore) {
        setTitle('Statistiques équipes');
      }
    } finally {
      if (!ignore) {
        setMetaLoading(false);
      }
    }
  };

  loadMeta();

  return () => {
    ignore = true;
  };
}, [selectedCompetitionIds]);

  const handleDisciplineChange = (event) => {
    const value = event.target.value;

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (value) nextParams.set('discipline', value);

    setSelectedSeasons([]);
    setSelectedCompetitions([]);

    setSearchParams(nextParams);
  };

  const handleSeasonsChange = (_, value) => {
    setSelectedSeasons(value);
    setSelectedCompetitions([]);

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (discipline) nextParams.set('discipline', discipline);

    if (value.length) {
      nextParams.set(
        'season',
        value.map((option) => option.value).join(',')
      );
    }

    if (value.length === 0 && seasonSinceParam) {
      nextParams.set('seasonSince', seasonSinceParam);
    }

    setSearchParams(nextParams);
  };

  const handleCompetitionsChange = (_, value) => {
    setSelectedCompetitions(value);

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (discipline) nextParams.set('discipline', discipline);

    if (selectedSeasons.length) {
      nextParams.set(
        'season',
        selectedSeasons.map((option) => option.value).join(',')
      );
    }

    if (!selectedSeasons.length && seasonSinceParam) {
      nextParams.set('seasonSince', seasonSinceParam);
    }

    if (value.length) {
      nextParams.set(
        'competitionIds',
        value.map((competition) => String(competition._id)).join(',')
      );
    }

    setSearchParams(nextParams);
  };

  const canRenderWidget =
    Boolean(discipline) && selectedCompetitionIds.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: '#e5e7eb',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Statistiques équipes
                </Typography>
                {discipline ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {DISCIPLINE_LABELS[discipline] || discipline}
                  </Typography>
                ) : null}
              </Box>

              <TextField
                select
                fullWidth
                label="Sport"
                value={discipline}
                onChange={handleDisciplineChange}
              >
                {DISCIPLINE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {!seasonSinceParam && (
                <Autocomplete
                  multiple
                  options={seasonOptions}
                  value={selectedSeasons}
                  loading={loadingSeasons}
                  onChange={handleSeasonsChange}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Saisons"
                      placeholder="Sélectionner une ou plusieurs saisons"
                    />
                  )}
                />
              )}

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={competitions}
                value={selectedCompetitions}
                loading={loadingCompetitions}
                onChange={handleCompetitionsChange}
                getOptionLabel={(option) =>
                  option?.season ? `${option.name} (${option.season})` : option?.name || ''
                }
                isOptionEqualToValue={(option, value) =>
                  String(option._id) === String(value._id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compétitions"
                    placeholder="Sélectionner une ou plusieurs compétitions"
                  />
                )}
              />

              {competitionsError ? (
                <Alert severity="error">{competitionsError}</Alert>
              ) : null}
            </Stack>
          </Paper>

          {canRenderWidget ? (
            metaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TeamRankingWidget
                provider={provider}
                discipline={discipline}
                season={widgetSeason}
                competitionIds={selectedCompetitionIds}
                title={title}
                onNavigate={(url) => navigate(url)}
              />
            )
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}